import React from 'react';
import {
  Image, Text, View, Document, StyleSheet, Link, Font, Page,
} from '@react-pdf/renderer';
import naturalisLogo from '../../assets/logos/naturalis.png';
import lnvLogo from '../../assets/logos/lnv.png';
import ecogoggleLogo from '../../assets/logos/ecogoggle.png';
import waarnemingenLogo from '../../assets/logos/waarnemingen.png';
import PdfFloweringArch from '../../components/FloweringArch/PdfFloweringArch';
import fontOpenSansRegular from '../../assets/fonts/opensans_regular.ttf';
import fontOpenSansSemiBold from '../../assets/fonts/opensans_semibold.ttf';
import fontOpenSansBold from '../../assets/fonts/opensans_bold.ttf';
import fontOpenSansItalic from '../../assets/fonts/opensans_italic.ttf';

const smallMargin = '20px';

const tableStyles = StyleSheet.create({
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderColor: '#bfbfbf',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
  },
  tableColHeader: {
    borderStyle: 'solid',
    borderColor: '#bfbfbf',
    borderBottomColor: '#000',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCol: {
    borderStyle: 'solid',
    borderColor: '#bfbfbf',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCellHeader: {
    margin: 5,
    fontSize: 12,
    fontWeight: 'semibold',
  },
  tableCell: {
    margin: 5,
    fontSize: 10,
  },
  summaryWrapper: {
    marginBottom: smallMargin,
  },
  title: {
    fontSize: 24,
    fontWeight: 'semibold',
    marginTop: smallMargin,
  },
  heading: {
    fontSize: 14,
    fontWeight: 'semibold',
  },
  bodyText: {
    fontSize: 12,
    marginBottom: 6,
  },
  italicText: {
    fontStyle: 'italic',
  },
  semibold: {
    fontWeight: 'semibold',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
  },
  logoWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  logo: {
    height: 'auto',
    width: 150,
  },
  partnerLogo: {
    width: 'auto',
    marginRight: 10,
  },
  stickToBottom: {
    marginTop: 'auto',
  },
  pagePadding: {
    padding: '35px 65px',
    fontFamily: 'Open Sans',
  },
  coverPagePadding: {
    padding: '65px',
  },
});

Font.register({
  family: 'Open Sans',
  fonts: [
    { src: fontOpenSansRegular },
    { src: fontOpenSansItalic, fontStyle: 'italic' },
    { src: fontOpenSansSemiBold, fontWeight: 'semibold' },
    { src: fontOpenSansBold, fontWeight: 700 },
  ],
});

const buildNameTable = (arrayItems, emptyText) => {
  if (!arrayItems || arrayItems.length === 0) {
    return (
      <Text style={[tableStyles.bodyText, tableStyles.italicText]}>
        {emptyText || 'Niet verstrekt.'}
      </Text>
    );
  }

  const withRedListProp = arrayItems[0].hasOwnProperty('isRedList');
  const colWidth = withRedListProp ? '33%' : '50%';
  const tableColHeaderStyle = [tableStyles.tableColHeader, { width: colWidth }];
  const tableColStyle = [tableStyles.tableCol, { width: colWidth }];

  return (
    <View style={tableStyles.table}>
      <View style={tableStyles.tableRow}>
        <View style={tableColHeaderStyle}>
          <Text style={tableStyles.tableCellHeader}>Nederlandse naam</Text>
        </View>
        <View style={tableColHeaderStyle}>
          <Text style={tableStyles.tableCellHeader}>Latijnse naam</Text>
        </View>
        {withRedListProp && (
          <View style={tableColHeaderStyle}>
            <Text style={tableStyles.tableCellHeader}>Rode lijst</Text>
          </View>
        )}
      </View>
      {arrayItems.map((item) => (
        <View style={tableStyles.tableRow} key={item.dutchName}>
          <View style={tableColStyle}>
            <Text style={tableStyles.tableCell}>{item.dutchName}</Text>
          </View>
          <View style={tableColStyle}>
            <Text style={tableStyles.tableCell}>{item.scientificName}</Text>
          </View>
          {withRedListProp && (
            <View style={tableColStyle}>
              <Text style={tableStyles.tableCell}>
                {item.isRedList ? 'Ja' : 'Nee'}
              </Text>
            </View>
          )}
        </View>
      ))}
    </View>
  );
};

const buildPageNumbering = () => (
  <Text
    style={tableStyles.pageNumber}
    render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
    fixed
  />
);

const PdfOverview = ({
                       mapPNG,
                       plantsInArea,
                       targetBees,
                       plantsChosenDuringAdviceFilteredExisting,
                       currentFloweringArch,
                       withAdviceFloweringArch,
                       coverageGoal,
                       beesById,
                       date,
                     }) => (
  <Document>
    <Page style={[tableStyles.pagePadding, tableStyles.coverPagePadding]}>
      <Image style={tableStyles.logo} src={naturalisLogo} />
      <Text style={tableStyles.title}>Bijvriendelijk beplanten</Text>
      <Text style={tableStyles.bodyText}>door Naturalis Biodiversity Center</Text>
      <Text style={[tableStyles.bodyText, tableStyles.italicText]}>
        Datum: {date}
      </Text>
      <View style={tableStyles.stickToBottom}>
        <View style={tableStyles.summaryWrapper}>
          <Text style={tableStyles.heading}>Samenvatting</Text>
          <Text style={tableStyles.bodyText}>
            Deze analyse is opgesteld met behulp van de zorgvuldig ontwikkelde
            Bloeibogen tool. In dit document vindt u gericht plantadvies dat is
            opgesteld op basis van de huidige voedselvoorziening van de gekozen
            doelsoorten bijen.
          </Text>
          <Text style={tableStyles.bodyText}>
            De informatie over de planten, bijen en hun interactie is afkomstig uit
            gegevens van Naturalis, EIS Kenniscentrum Insecten, www.waarneming.nl en
            het Nederlands soortenregister.
          </Text>
          <Text style={tableStyles.bodyText}>
            Gebruik de tool gratis via <Link src="www.bloeibogen.nl">www.bloeibogen.nl</Link>.
          </Text>
        </View>
        <Text style={[tableStyles.bodyText, tableStyles.semibold]}>Partners</Text>
        <View style={tableStyles.logoWrapper}>
          <Image style={[tableStyles.partnerLogo, { height: '45px' }]} src={lnvLogo} />
          <Image style={[tableStyles.partnerLogo, { height: '25px' }]} src={waarnemingenLogo} />
          <Image style={[tableStyles.partnerLogo, { height: '30px' }]} src={ecogoggleLogo} />
        </View>
      </View>
    </Page>

    <Page style={tableStyles.pagePadding}>
      <Text style={tableStyles.heading}>1. Geselecteerd gebied</Text>
      <Text style={tableStyles.bodyText}>
        Onderstaand is het door de gebruiker geselecteerde gebied. Op basis van dit gebied zijn de
        mogelijk voorkomende plant- en bijensoorten berekend.
      </Text>
      {mapPNG && <Image src={mapPNG} />}
      {buildPageNumbering()}
    </Page>

    <Page style={tableStyles.pagePadding}>
      <Text style={tableStyles.heading}>
        2. Bestaande beplanting ({plantsInArea.length})
      </Text>
      <Text style={tableStyles.bodyText}>
        Deze beplanting is door de gebruiker als reeds aanwezige beplanting aangegeven.
      </Text>
      {buildNameTable(plantsInArea, 'In het geselecteerde gebied is reeds nog geen beplanting aanwezig.')}
      {buildPageNumbering()}
    </Page>

    <Page style={tableStyles.pagePadding}>
      <Text style={tableStyles.heading}>
        3. Gekozen doelsoorten bijen ({targetBees.length})
      </Text>
      <Text style={tableStyles.bodyText}>
        De onderstaande bijensoorten zijn als doelsoort gekozen. Op basis van de voedselbehoeften van
        deze bijensoorten en de, bovenstaande, ingevulde beplanting is het plantadvies gegeven.
      </Text>
      {buildNameTable(targetBees)}
      {buildPageNumbering()}
    </Page>

    <Page style={tableStyles.pagePadding} orientation="landscape">
      <Text style={tableStyles.heading}>4. Bloeiboog huidige situatie</Text>
      <Text style={tableStyles.bodyText}>
        Deze bloeiboog visualiseert de vliegmaanden van de bijensoorten en laat zien hoeveel
        plantensoorten er in het gebied aanwezig zijn waarvan de bij zijn voeding kan halen.
      </Text>
      <Text style={tableStyles.bodyText}>
        Het doel is om voor elke bij, in elke vliegmaand, {coverageGoal} verschillende bloeiende
        plantensoorten beschikbaar te hebben.
      </Text>
      <Text style={[tableStyles.bodyText, tableStyles.semibold]}>
        Gemiddelde voedseldekking: {currentFloweringArch.coverage.overall || '0'}%
      </Text>
      <PdfFloweringArch
        archInfo={currentFloweringArch}
        coverageGoal={coverageGoal}
        beesById={beesById}
      />
      {buildPageNumbering()}
    </Page>

    <Page style={tableStyles.pagePadding}>
      <Text style={tableStyles.heading}>
        5. Planten gekozen gedurend advies ({plantsChosenDuringAdviceFilteredExisting.length})
      </Text>
      {buildNameTable(plantsChosenDuringAdviceFilteredExisting, 'Geen beplanting gekozen gedurende het advies.')}
      {buildPageNumbering()}
    </Page>

    <Page style={tableStyles.pagePadding} orientation="landscape">
      <Text style={tableStyles.heading}>6. Bloeiboog na advies</Text>
      <Text style={tableStyles.bodyText}>
        Deze bloeiboog visualiseert de situatie na het opvolgen vvan het plantadvies.
      </Text>
      <Text style={tableStyles.bodyText}>
        Het doel is om voor elke bij, in elke vliegmaand, {coverageGoal} verschillende bloeiende
        plantensoorten beschikbaar te hebben.
      </Text>
      <Text style={[tableStyles.bodyText, tableStyles.semibold]}>
        Gemiddelde voedseldekking: {withAdviceFloweringArch.coverage.overall || '0'}%
      </Text>
      <PdfFloweringArch
        archInfo={withAdviceFloweringArch}
        coverageGoal={coverageGoal}
        beesById={beesById}
      />
      {buildPageNumbering()}
    </Page>
  </Document>
);

export default PdfOverview;